import React, { useEffect, useRef, useState } from 'react';
import { getBackendService } from '../../shared/services/apiConfig';
import type { User, Restaurant } from '../../shared/types';
import { Button } from '../../shared/design';
import AtableLogo from '../../shared/design/components/AtableLogo';
import { safeStorageImageUrl } from '../../shared/restaurantShowcase';

interface HeaderProps {
    onNavigate?: (view: string) => void;
    onOpenNav?: () => void;
    /** Tiroir de navigation ouvert (aria-expanded du bouton menu). */
    navOpen?: boolean;
    onValidationManuelle?: () => void;
    onScanClient?: () => void;
}

const todayLabel = () => {
    const s = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const initials = (name?: string) =>
    (name || 'R').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');

/**
 * Barre supérieure volontairement fine : la date du jour, les actions liées à
 * l'écran (quand il y en a) et le compte. Le titre de page appartient à la page.
 */
const Header: React.FC<HeaderProps> = ({ onNavigate, onOpenNav, navOpen = false, onValidationManuelle, onScanClient }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Photo du lieu en échec de chargement (fichier retiré, hors ligne…) → initiales.
    const [avatarFailed, setAvatarFailed] = useState(false);

    useEffect(() => {
        let isMounted = true;
        let unsubscribe: (() => void) | undefined;
        const load = async () => {
            try {
                const backend = await getBackendService();
                const currentUser = backend.getCurrentUser();
                if (!isMounted) return;
                setUser(currentUser);
                if (currentUser?.restaurantId) {
                    const r = await backend.getRestaurant(
                        currentUser.restaurantId
                    );
                    if (isMounted) setRestaurant(r);
                }
                // Une seule inscription : load() est rappelé à chaque notification
                // (ex. nouvelle photo du lieu enregistrée dans « Mon établissement »).
                if (isMounted && backend.subscribe && !unsubscribe) {
                    unsubscribe = backend.subscribe(load);
                }
            } catch (error) {
                console.error('Error loading header restaurant:', error);
            }
        };
        load();
        return () => {
            isMounted = false;
            unsubscribe?.();
        };
    }, []);

    const avatarUrl = safeStorageImageUrl(restaurant?.imageUrl);
    useEffect(() => { setAvatarFailed(false); }, [avatarUrl]);

    useEffect(() => {
        if (!showMenu) return;
        const onDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowMenu(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [showMenu]);

    const handleLogout = async () => {
        const backend = await getBackendService();
        backend.logout();
        window.location.reload();
    };

    const menuItem = (label: string, onClick: () => void, icon: React.ReactNode, danger = false) => (
        <button
            type="button"
            onClick={() => { setShowMenu(false); onClick(); }}
            className={`flex items-center gap-3 w-full px-3 h-9 rounded-lg text-sm transition-colors ${
                danger
                    ? 'text-brand-600 dark:text-brand-300 font-medium hover:bg-brand-50 dark:hover:bg-brand-500/10'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
        >
            <span aria-hidden="true" className={`inline-flex [&>svg]:w-[18px] [&>svg]:h-[18px] ${danger ? '' : 'text-gray-500'}`}>{icon}</span>
            <span>{label}</span>
        </button>
    );

    const restaurantName = restaurant?.name || user?.restaurantName || 'Mon restaurant';

    return (
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 bg-cream/90 backdrop-blur-md border-b border-black/[0.08]">
            {/* Gauche : menu mobile + logo mobile / date desktop */}
            <div className="flex items-center gap-3 min-w-0">
                <button
                    type="button"
                    onClick={onOpenNav}
                    className="lg:hidden w-10 h-10 -ml-2 rounded-full flex items-center justify-center text-gray-700 hover:bg-white/70"
                    aria-label="Ouvrir le menu"
                    aria-expanded={navOpen}
                    aria-controls="restaurateur-nav-drawer"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" /></svg>
                </button>
                <span className="lg:hidden"><AtableLogo variant="wordmark" height={26} /></span>
                <p className="hidden lg:block text-sm text-gray-500 dark:text-gray-400 truncate">{todayLabel()}</p>
            </div>

            {/* Droite : actions d'écran + compte */}
            <div className="flex items-center gap-2 sm:gap-3">
                {onValidationManuelle && (
                    <Button size="sm" variant="outline" onClick={onValidationManuelle} title="Validation manuelle (panne NFC)">
                        <span className="hidden sm:inline">Validation manuelle</span>
                        <span className="sm:hidden">Manuel</span>
                    </Button>
                )}
                {onScanClient && (
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={onScanClient}
                        title="Scanner le QR d'un client"
                        leftIcon={
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3M4 12h16" />
                            </svg>
                        }
                    >
                        <span className="hidden sm:inline">Scanner un client</span>
                        <span className="sm:hidden">Scanner</span>
                    </Button>
                )}

                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        onClick={() => setShowMenu(v => !v)}
                        aria-expanded={showMenu}
                        aria-controls="account-menu"
                        aria-label={`Compte : ${restaurantName}`}
                        className="flex items-center gap-2.5 pl-2.5 pr-1.5 h-10 rounded-lg hover:bg-black/[0.04] transition-colors"
                    >
                        <div className="hidden md:block text-right leading-tight">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 max-w-[200px] truncate">{restaurantName}</p>
                            <p className="text-[12px] text-gray-500 dark:text-gray-400 max-w-[200px] truncate">{restaurant?.address || user?.email || ''}</p>
                        </div>
                        {avatarUrl && !avatarFailed ? (
                            <img
                                className="h-8 w-8 rounded-full object-cover"
                                src={avatarUrl}
                                onError={() => setAvatarFailed(true)}
                                alt=""
                            />
                        ) : (
                            <span className="h-8 w-8 rounded-full bg-brand-500 text-cream text-xs font-semibold flex items-center justify-center">
                                {initials(restaurantName)}
                            </span>
                        )}
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${showMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {showMenu && (
                        // Simple panneau de liens (bouton qui déplie) : pas de role="menu", dont le
                        // clavier (flèches) n'est pas implémenté ; Tab et Échap suffisent.
                        <nav id="account-menu" aria-label="Menu du compte" className="absolute right-0 mt-2 w-64 p-1.5 bg-white rounded-xl shadow-lg border border-black/[0.08] z-50 animate-fade-in-up">
                            <div className="px-3 py-2.5 mb-1">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{restaurantName}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                            </div>
                            {menuItem('Mon établissement', () => onNavigate?.('profile-editor'),
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9l1.5-5h15L21 9M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 11v9h14v-9" /></svg>)}
                            {menuItem('Abonnement & facturation', () => onNavigate?.('billing'),
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18v10H3zM3 10h18M7 14h4" /></svg>)}
                            {menuItem('Paramètres', () => onNavigate?.('settings'),
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7.4-3a7.4 7.4 0 0 0-.1-1l2-1.6-2-3.4-2.4 1a7.6 7.6 0 0 0-1.7-1L14.8 3H9.2l-.4 2.6a7.6 7.6 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 0 0 1.7 1l.4 2.6h5.6l.4-2.6a7.6 7.6 0 0 0 1.7-1l2.4 1 2-3.4-2-1.6c.1-.3.1-.7.1-1z" /></svg>)}
                            {menuItem('Aide & support', () => onNavigate?.('help'),
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2-3 4m0 3.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" /></svg>)}
                            <div className="my-1 border-t border-black/[0.06] dark:border-gray-800" />
                            {menuItem('Se déconnecter', handleLogout,
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M16 17l5-5-5-5M21 12H9m4 7a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3" /></svg>, true)}
                        </nav>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
